/**
 * Net Worth Calculations Utility
 * Provides functions for calculating net worth, assets, and liabilities
 */

import { convertCurrency } from '../services/exchangeRateService';

/**
 * Get the current market value of an investment
 *
 * When live prices are provided via priceMap, calculates: quantity × current price
 * When no live prices available, falls back to cost basis values
 *
 * Priority with live prices: (quantity × livePrice) > cost basis fallback
 * Priority without live prices: totalCostBasis > (quantity × purchasePrice) > amount
 *
 * @param {Object} inv - Investment object
 * @param {Object} priceMap - Optional map of ticker → { price, currency, ... } from live pricing
 * @returns {number} Investment market value (live) or cost basis (fallback)
 */
export const getInvestmentMarketValue = (inv, priceMap = null) => {
  // If we have live prices and this investment has a ticker, use live pricing
  if (priceMap && inv.ticker && inv.quantity && inv.quantity > 0) {
    const priceData = priceMap[inv.ticker];
    if (priceData && priceData.price && priceData.price > 0) {
      return inv.quantity * priceData.price;
    }
  }

  // Fall back to cost basis when no live prices available
  // Use totalCostBasis if available (pre-calculated cost basis)
  if (inv.totalCostBasis && inv.totalCostBasis > 0) {
    return inv.totalCostBasis;
  }

  // Calculate from quantity and purchase price if available
  const purchasePrice = inv.purchasePrice || inv.averageCostBasis || inv.pricePerUnit;
  if (inv.quantity && inv.quantity > 0 && purchasePrice && purchasePrice > 0) {
    return inv.quantity * purchasePrice;
  }

  // Fall back to amount (manual entry / amount-only investments)
  return parseFloat(inv.amount) || 0;
};

/**
 * Calculate total value of investments/portfolio
 * Uses live market values when priceMap provided, falls back to cost basis
 *
 * @param {Array} investments - Array of investment objects
 * @param {string} displayCurrency - Target display currency (USD, GBP, EUR)
 * @param {Object} exchangeRates - Exchange rates object (optional)
 * @param {Object} priceMap - Optional map of ticker → { price, currency, ... } from live pricing
 * @returns {number} Total investment value in display currency
 */
export const calculateTotalInvestments = (investments = [], displayCurrency = null, exchangeRates = null, priceMap = null) => {
  // If no exchange rates provided, just sum market values (legacy behavior)
  if (!exchangeRates || !displayCurrency) {
    return investments.reduce((total, inv) => total + getInvestmentMarketValue(inv, priceMap), 0);
  }

  // Convert each investment to display currency using market values
  return investments.reduce((total, inv) => {
    const marketValue = getInvestmentMarketValue(inv, priceMap);
    const investmentCurrency = inv.currency || displayCurrency; // Default to display currency for legacy investments

    // Convert to display currency
    const convertedAmount = convertCurrency(marketValue, investmentCurrency, displayCurrency, exchangeRates);

    return total + convertedAmount;
  }, 0);
};

/**
 * Calculate total cryptocurrency value
 * @param {Array} cryptoHoldings - Array of crypto holdings
 * @returns {number} Total crypto value
 */
export const calculateTotalCrypto = (cryptoHoldings = []) => {
  return cryptoHoldings.reduce((total, holding) => {
    const value = (parseFloat(holding.quantity) || 0) * (parseFloat(holding.currentPrice) || 0);
    return total + value;
  }, 0);
};

/**
 * Calculate total liabilities
 * ⭐ MULTI-CURRENCY: Now supports conversion to display currency
 *
 * @param {Array} liabilities - Array of liability objects
 * @param {string} displayCurrency - Target display currency (USD, GBP, EUR)
 * @param {Object} exchangeRates - Exchange rates object (optional)
 * @returns {number} Total liabilities in display currency
 */
export const calculateTotalLiabilities = (liabilities = [], displayCurrency = null, exchangeRates = null) => {
  // If no exchange rates provided, just sum amounts (legacy behavior)
  if (!exchangeRates || !displayCurrency) {
    return liabilities.reduce((total, liability) => {
      return total + (parseFloat(liability.currentBalance) || 0);
    }, 0);
  }

  // ⭐ NEW: Convert each liability to display currency
  return liabilities.reduce((total, liability) => {
    const amount = parseFloat(liability.currentBalance) || 0;
    const liabilityCurrency = liability.currency || displayCurrency; // Default to display currency for legacy liabilities

    // Convert to display currency
    const convertedAmount = convertCurrency(amount, liabilityCurrency, displayCurrency, exchangeRates);

    return total + convertedAmount;
  }, 0);
};

/**
 * Calculate net worth
 * ⭐ MULTI-CURRENCY: Now supports currency conversion
 * ⭐ LIVE PRICING: Now supports live prices via priceMap
 *
 * @param {Object} params
 * @param {Array} params.investments - Investment holdings
 * @param {Array} params.cryptoHoldings - Crypto holdings
 * @param {Array} params.liabilities - Liabilities
 * @param {number} params.cashReserves - Cash and savings
 * @param {string} params.displayCurrency - Display currency (USD, GBP, EUR)
 * @param {Object} params.exchangeRates - Exchange rates object
 * @param {Object} params.priceMap - Optional map of ticker → { price, currency, ... } from live pricing
 * @returns {Object} Net worth breakdown in display currency
 */
export const calculateNetWorth = ({
  investments = [],
  cryptoHoldings = [],
  liabilities = [],
  cashReserves = 0,
  displayCurrency = null,
  exchangeRates = null,
  priceMap = null
}) => {
  // ⭐ MULTI-CURRENCY + LIVE PRICING: Pass exchange rates and prices to investment calculations
  const totalInvestments = calculateTotalInvestments(investments, displayCurrency, exchangeRates, priceMap);
  const totalCrypto = calculateTotalCrypto(cryptoHoldings);
  const totalCash = parseFloat(cashReserves) || 0;
  const totalLiabilities = calculateTotalLiabilities(liabilities, displayCurrency, exchangeRates);

  const totalAssets = totalInvestments + totalCrypto + totalCash;
  const netWorth = totalAssets - totalLiabilities;

  return {
    totalAssets,
    totalInvestments,
    totalCrypto,
    totalCash,
    totalLiabilities,
    netWorth,
    displayCurrency, // ⭐ NEW: Include display currency in result
    breakdown: {
      investments: totalInvestments,
      crypto: totalCrypto,
      cash: totalCash,
      liabilities: totalLiabilities
    }
  };
};

/**
 * Group liabilities by type
 * ⭐ MULTI-CURRENCY: Now supports conversion to display currency
 *
 * @param {Array} liabilities - Array of liability objects
 * @param {string} displayCurrency - Target display currency (USD, GBP, EUR)
 * @param {Object} exchangeRates - Exchange rates object (optional)
 * @returns {Object} Liabilities grouped by type with converted totals
 */
export const groupLiabilitiesByType = (liabilities = [], displayCurrency = null, exchangeRates = null) => {
  const grouped = {};

  liabilities.forEach(liability => {
    const type = liability.type || 'Other';
    if (!grouped[type]) {
      grouped[type] = {
        type,
        items: [],
        total: 0
      };
    }

    grouped[type].items.push(liability);

    // ⭐ MULTI-CURRENCY: Convert liability amount to display currency
    const amount = parseFloat(liability.currentBalance) || 0;
    if (exchangeRates && displayCurrency) {
      const liabilityCurrency = liability.currency || displayCurrency;
      const convertedAmount = convertCurrency(amount, liabilityCurrency, displayCurrency, exchangeRates);
      grouped[type].total += convertedAmount;
    } else {
      // Legacy behavior: just sum amounts
      grouped[type].total += amount;
    }
  });

  return grouped;
};

/**
 * Calculate monthly liability payments
 * ⭐ MULTI-CURRENCY: Now supports conversion to display currency
 *
 * @param {Array} liabilities - Array of liability objects
 * @param {string} displayCurrency - Target display currency (USD, GBP, EUR)
 * @param {Object} exchangeRates - Exchange rates object (optional)
 * @returns {number} Total monthly payments in display currency
 */
export const calculateMonthlyLiabilityPayments = (liabilities = [], displayCurrency = null, exchangeRates = null) => {
  return liabilities.reduce((total, liability) => {
    const amount = parseFloat(liability.monthlyPayment) || 0;

    // ⭐ MULTI-CURRENCY: Convert monthly payment to display currency
    if (exchangeRates && displayCurrency) {
      const liabilityCurrency = liability.currency || displayCurrency;
      const convertedAmount = convertCurrency(amount, liabilityCurrency, displayCurrency, exchangeRates);
      return total + convertedAmount;
    }

    // Legacy behavior: just sum amounts
    return total + amount;
  }, 0);
};

/**
 * Calculate debt-to-asset ratio
 * @param {number} totalAssets
 * @param {number} totalLiabilities
 * @returns {number} Debt-to-asset ratio (percentage)
 */
export const calculateDebtToAssetRatio = (totalAssets, totalLiabilities) => {
  if (totalAssets === 0) return 0;
  return (totalLiabilities / totalAssets) * 100;
};

/**
 * Get net worth trend color
 * @param {number} netWorth
 * @returns {string} Color code
 */
export const getNetWorthColor = (netWorth) => {
  if (netWorth >= 0) return '#10b981'; // green
  return '#ef4444'; // red
};

/**
 * Calculate financial health score (0-100)
 * @param {Object} params
 * @returns {Object} Health score and rating
 */
export const calculateFinancialHealthScore = ({
  netWorth,
  totalAssets,
  totalLiabilities,
  monthlyIncome = 0,
  monthlyExpenses = 0
}) => {
  let score = 0;
  const factors = [];

  // Factor 1: Net Worth (25 points)
  if (netWorth > 0) {
    score += 25;
    factors.push({ name: 'Positive Net Worth', points: 25 });
  } else if (netWorth > -50000) {
    score += 10;
    factors.push({ name: 'Low Negative Net Worth', points: 10 });
  }

  // Factor 2: Debt-to-Asset Ratio (25 points)
  const debtRatio = calculateDebtToAssetRatio(totalAssets, totalLiabilities);
  if (debtRatio < 20) {
    score += 25;
    factors.push({ name: 'Excellent Debt Ratio', points: 25 });
  } else if (debtRatio < 40) {
    score += 20;
    factors.push({ name: 'Good Debt Ratio', points: 20 });
  } else if (debtRatio < 60) {
    score += 15;
    factors.push({ name: 'Moderate Debt Ratio', points: 15 });
  } else if (debtRatio < 80) {
    score += 10;
    factors.push({ name: 'High Debt Ratio', points: 10 });
  } else {
    factors.push({ name: 'Very High Debt Ratio', points: 0 });
  }

  // Factor 3: Asset Diversification (25 points)
  if (totalAssets > 0) {
    score += 25;
    factors.push({ name: 'Has Assets', points: 25 });
  }

  // Factor 4: Cash Flow (25 points)
  if (monthlyIncome > monthlyExpenses && monthlyIncome > 0) {
    score += 25;
    factors.push({ name: 'Positive Cash Flow', points: 25 });
  } else if (monthlyIncome > 0) {
    score += 10;
    factors.push({ name: 'Break-even Cash Flow', points: 10 });
  }

  // Determine rating
  let rating = 'Poor';
  let color = '#ef4444';

  if (score >= 80) {
    rating = 'Excellent';
    color = '#10b981';
  } else if (score >= 60) {
    rating = 'Good';
    color = '#3b82f6';
  } else if (score >= 40) {
    rating = 'Fair';
    color = '#f59e0b';
  } else if (score >= 20) {
    rating = 'Poor';
    color = '#f97316';
  } else {
    rating = 'Critical';
    color = '#ef4444';
  }

  return {
    score,
    rating,
    color,
    factors
  };
};
